import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './ArchivePage.module.scss'
import { Button, Layout } from "components/index";
import { useTranslation } from 'react-i18next'
import { getActivityLog } from 'service/ArchiveService'

const SVG_ARCHIVE = (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 6H4V4h16v2zm1 2H3a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h18a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1zm-1 11H4v-9h16v9zm-7-7H9v2h4v-2z"/>
  </svg>
)
const SVG_UNIVERSITY = (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 3 1 9l4 2.18V16h2v-3.82L9 13.4V16c0 1.66 1.34 3 3 3s3-1.34 3-3v-2.6l2-1.09V16h2v-4.82L23 9 12 3zm0 2.33L18.28 9 12 12.14 5.72 9 12 5.33zM14 16c0 1.1-.9 2-2 2s-2-.9-2-2v-1.46l2 1.09 2-1.09V16z"/>
  </svg>
)
const SVG_STUDENT = (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
  </svg>
)
const SVG_EMPLOYEE = (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 7H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V9c0-1.1-.9-2-2-2zm0 12H4V9h16v10zM12 4c1.1 0 2 .9 2 2h2c0-2.21-1.79-4-4-4S8 3.79 8 6h2c0-1.1.9-2 2-2zm-1 10h2v-5h-2v5z"/>
  </svg>
)
const SVG_LOG = (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 10h2v7H7v-7zm4-3h2v10h-2V7zm4 6h2v4h-2v-4z"/>
  </svg>
)
const SVG_ARROW = (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
  </svg>
)
const SVG_INFO = (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
  </svg>
)

function ArchivePage() {
  const { t } = useTranslation('common')

  const navigate = useNavigate()
  const [stats, setStats] = useState({
    university: { total: 0, digitized: 0 },
    students: { total: 0, digitized: 0 },
    employees: { total: 0, digitized: 0 },
    logs: { total: 0 }
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      setLoading(true)
      const activityData = await getActivityLog()
      setStats(prevStats => ({
        ...prevStats,
        logs: { total: activityData.length || 0 }
      }))
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }

  const archiveOptions = [
    {
      id: 'university',
      title: 'Архив университета',
      description: 'Фонды, описи, дела, приказы и административные документы',
      iconClass: 'icon_blue',
      icon: SVG_UNIVERSITY,
      stats: `${stats.university.total} фондов`
    },
    {
      id: 'students',
      title: 'Архив студентов',
      description: 'Личные дела студентов, ведомости, дипломы, справки',
      iconClass: 'icon_green',
      icon: SVG_STUDENT,
      stats: `${stats.students.total} личных дел`
    },
    {
      id: 'employees',
      title: 'Архив сотрудников',
      description: 'Кадровые личные дела, трудовые договоры, аттестации',
      iconClass: 'icon_orange',
      icon: SVG_EMPLOYEE,
      stats: `${stats.employees.total} личных дел`
    },
    {
      id: 'logs',
      title: 'Журнал действий',
      description: 'История всех операций в архивной системе',
      iconClass: 'icon_purple',
      icon: SVG_LOG,
      stats: `${stats.logs.total} записей`
    }
  ]

  return (
    <Layout>
      <div className={styles.archive_page}>
        <div className={styles.page_header}>
          <div className={styles.header_icon}>{SVG_ARCHIVE}</div>
          <div className={styles.header_text}>
            <h1>{t('archive.system_title')}</h1>
            <p>{t('archive.system_subtitle')}</p>
          </div>
          <div className={styles.header_actions}>
            <button
              className={styles.btn_action}
              onClick={() => navigate('/archive/students/')}
            >
              + Студент
            </button>
            <button
              className={`${styles.btn_action} ${styles.btn_orange}`}
              onClick={() => navigate('/archive/employees/')}
            >
              + Дело сотрудника
            </button>
          </div>
        </div>

        <div className={styles.archive_grid}>
          {archiveOptions.map((archive) => (
            <div
              key={archive.id}
              className={styles.archive_card}
              onClick={() => navigate(`/archive/${archive.id}/`)}
            >
              <div className={`${styles.card_icon_wrap} ${styles[archive.iconClass]}`}>
                {archive.icon}
              </div>
              <h2 className={styles.card_title}>{archive.title}</h2>
              <p className={styles.card_description}>{archive.description}</p>
              <p className={styles.card_stats}>{archive.stats}</p>
              <div className={styles.card_arrow}>
                Открыть {SVG_ARROW}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.info_section}>
          <div className={styles.info_title}>{SVG_INFO} Регламент хранения документов</div>
          <ul className={styles.info_list}>
            <li>{t('archive.rule_1')}</li>
            <li>{t('archive.rule_2')}</li>
            <li>{t('archive.rule_3')}</li>
            <li>{t('archive.rule_4')}</li>
            <li>{t('archive.rule_5')}</li>
            <li>{t('archive.rule_6')}</li>
          </ul>
        </div>
      </div>
    </Layout>
  )
}

export default ArchivePage
